import { ExportV2 } from "@robin/common";
import { Datapoint, SeriesType } from "../hooks/useVisualisationData";
import { useTranslation } from "react-i18next";
import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Paper,
  Typography,
  TypographyProps,
} from "@mui/material";
import { HorizontalStack } from "./primitives/Stacks";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import SubjectIcon from "@mui/icons-material/Subject";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import { DateTime } from "./primitives/DateTime";

const subheaderPrimaryTypographyProps: TypographyProps = {
  color: "text.primary",
  variant: "subtitle2",
};

const ChartPopover = ({
  data,
  dataIndex,
  series,
  dataset,
  activities,
}: {
  data: {
    [key: string]: number | null;
    timestamp: ExportV2.Timestamp;
  }[];
  dataIndex: number | undefined;
  series: SeriesType[];
  dataset: Datapoint[];
  activities: ExportV2.PositiveActivity[] | undefined;
}) => {
  const { t } = useTranslation();

  if (dataIndex === undefined) return null;

  const displayData = data[dataIndex] || {};
  const additionalData = dataset.find(
    (i) => i.timestamp === displayData.timestamp
  );
  const {
    experiences = [],
    notes = [],
    positiveActivities = [],
  } = additionalData?.extras || {};

  return (
    <Paper elevation={8} sx={{ pb: 1 }}>
      <List dense>
        <ListSubheader component={ListItem}>
          <ListItemText
            primary={<DateTime timestamp={displayData.timestamp} />}
            primaryTypographyProps={{ ...subheaderPrimaryTypographyProps }}
          />
        </ListSubheader>
        <Divider component="li" sx={{ my: 1 }} />
        {Object.entries(displayData).map(([key, value]) => {
          const serie = series.find((s: SeriesType) => s.id == key);
          if (key === "timestamp" || !serie) return null;
          return (
            <ListItem key={key}>
              <HorizontalStack
                alignItems="center"
                spacing={2}
                sx={{ width: "100%" }}
              >
                <Box
                  sx={{
                    background: serie.color,
                    borderRadius: 4,
                    height: 8,
                    width: 8,
                  }}
                />
                <Typography color="text.secondary" flexGrow={1} variant="body2">
                  {serie.name}:
                </Typography>
                <Typography
                  align="right"
                  color="text.primary"
                  flexGrow={1}
                  variant="subtitle2"
                >
                  {value}
                </Typography>
              </HorizontalStack>
            </ListItem>
          );
        })}
      </List>
      {!!experiences.length && (
        <List
          dense
          disablePadding
          subheader={
            <ListSubheader component={ListItem}>
              <ListItemText
                primary={`${t("tabs.experiences.experiences-label")}:`}
                primaryTypographyProps={{ ...subheaderPrimaryTypographyProps }}
              />
            </ListSubheader>
          }
        >
          {experiences.map((i: ExportV2.Experience) => (
            <ListItem key={i.id} sx={{ paddingY: 0 }}>
              <ListItemIcon sx={{ minWidth: "auto", mr: 2 }}>
                <FavoriteBorderIcon sx={{ fontSize: 16 }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography color="text.secondary" variant="body2">
                    {i.title}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
      {!!notes.length && (
        <List
          dense
          subheader={
            <ListSubheader component={ListItem}>
              <ListItemText
                primary={`${t("tabs.more.notes-label")}:`}
                primaryTypographyProps={{ ...subheaderPrimaryTypographyProps }}
              />
            </ListSubheader>
          }
        >
          {notes.map((i: ExportV2.Note) => (
            <ListItem key={i.id} sx={{ paddingY: 0 }}>
              <ListItemIcon sx={{ minWidth: "auto", mr: 2 }}>
                <SubjectIcon sx={{ fontSize: 16 }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography color="text.secondary" variant="body2">
                    {i.note}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
      {!!positiveActivities.length && (
        <List
          dense
          subheader={
            <ListSubheader component={ListItem}>
              <ListItemText
                primary={`${t("tabs.activities.activities-label")}:`}
                primaryTypographyProps={{ ...subheaderPrimaryTypographyProps }}
              />
            </ListSubheader>
          }
        >
          {positiveActivities.map((i: ExportV2.PositiveActivityLog) => {
            const { name = "" } =
              activities?.find((a) => a.id === i.positiveActivityId) || {};
            return (
              <ListItem key={i.id} sx={{ paddingY: 0 }}>
                <ListItemIcon sx={{ minWidth: "auto", mr: 2 }}>
                  <StarOutlineIcon sx={{ fontSize: 16 }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography color="text.secondary" variant="body2">
                      {t(name)}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      )}
    </Paper>
  );
};

export default ChartPopover;
